import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PlusIcon from '../../../../../../components/icons/PlusIcon';
import Button from '../../../../../../components/global/Button';
import UserAddressDisplay from '../../../../../../components/global/user-address/UserAddressDisplay';
import AccountCard from '../../../../components/AccountCard';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../../../constants';
import DropdownMenu from '../../../../../../components/global/DropdownMenu';
import DotTrigger from '../../../../../../components/global/DotTrigger';
import PostcardIcon from '../../../../../../components/icons/PostcardIcon';
import { getAccountCardElementsConfiguration } from '../../../../components/AccountCard/helpers';
import CheckboxIcon from '../../../../../../components/icons/CheckboxIcon';

const propTypes = {
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
    account_card_sides: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        card_side: PropTypes.shape({
          card_template: PropTypes.object.isRequired,
          card_element_configurations: PropTypes.array.isRequired,
        }).isRequired,
        card_element_configurations: PropTypes.array.isRequired,
      }).isRequired,
    ).isRequired,
  }).isRequired,
  cardElementLoading: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onEdit: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func,
  onAddRecipients: PropTypes.func,
  onElementConfigurationChange: PropTypes.func.isRequired,
  checkingOut: PropTypes.bool,
  hasCartItems: PropTypes.bool,
};

const AccountCardReview = ({
  accountCard,
  cardElementLoading,
  onEdit,
  onCheckout,
  onAddToCart,
  onAddRecipients,
  onElementConfigurationChange,
  checkingOut,
  hasCartItems,
}) => {
  if (!accountCard) return null;

  const state = getAccountCardElementsConfiguration(
    accountCard,
    cardElementLoading,
  );
  const [side, setSide] = useState(CARD_SIDE.FRONT);

  const addressElement = state.find(
    element => element.cardElement.type === CARD_ELEMENT_TYPE.ADDRESS,
  );

  const externalShippingAddresses =
    addressElement?.cardElementConfiguration?.external_shipping_addresses
      ?.records ?? [];

  const rosterIdentityAddresses =
    addressElement?.cardElementConfiguration
      ?.roster_identity_linked_identity_addresses?.records ?? [];

  const useFamilyName =
    !!addressElement?.cardElementConfiguration?.use_family_name;

  const onRemoveAddress = (address, isRoster) => {
    if (addressElement) {
      if (isRoster) {
        onElementConfigurationChange(addressElement, {
          roster_identity_linked_identity_address_ids: rosterIdentityAddresses
            .map(addr => addr.id)
            .filter(id => id !== address.id),
        });
      } else {
        onElementConfigurationChange(addressElement, {
          external_shipping_address_ids: externalShippingAddresses
            .map(addr => addr.id)
            .filter(id => id !== address.id),
        });
      }
    }
  };

  const renderAddress = (address, isRoster) => (
    <UserAddressDisplay
      key={address.id}
      address={address}
      familyName={useFamilyName}
      className="account-card-review-recipient"
    >
      <div className="dropdown-menu-dark">
        <DropdownMenu
          placement="bottom-end"
          toggle={<DotTrigger dotType="vertical" />}
        >
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={() => onRemoveAddress(address, isRoster)}
          >
            Remove
          </Button>
        </DropdownMenu>
      </div>
    </UserAddressDisplay>
  );

  return (
    <div className="account-card-review">
      <div className="d-flex flex-column flex-lg-row h-100">
        <div className="w-100 pr-0 pr-lg-4">
          <h4 className="d-flex">
            How it looks{' '}
            <Button
              btnStyle="link"
              onClick={onEdit}
              className="ml-auto text-bold"
            >
              Edit
            </Button>{' '}
          </h4>
          <p className="text-sm">
            This is how your card will look when it is printed.
          </p>
          <AccountCard
            onElementConfigurationChange={() => {}}
            side={side}
            onSideChange={prev =>
              prev === CARD_SIDE.FRONT
                ? setSide(CARD_SIDE.BACK)
                : setSide(CARD_SIDE.FRONT)
            }
            accountCard={accountCard}
            disablePhotoWatermarkOverlay
            viewOnly
          />
        </div>
        <div className="w-100 pl-0 pl-lg-4 d-flex flex-column h-100">
          <h4 className="d-flex">
            Your recipients{' '}
            {onAddRecipients && (
              <Button
                btnStyle="link"
                onClick={onAddRecipients}
                className="ml-auto d-flex align-items-center text-bold"
              >
                <PlusIcon className="mr-1" color="#0d84fe" size={14} />
                Add more
              </Button>
            )}
          </h4>
          <div className="account-card-review-recipients-scroll">
            {externalShippingAddresses.map(address =>
              renderAddress(address, false),
            )}
            {rosterIdentityAddresses.map(address =>
              renderAddress(address, true),
            )}
          </div>
        </div>
      </div>
      <div className="account-card-review-footer">
        <div className="account-card-review-footer-buttons">
          {onAddToCart && (
            <Button
              btnBlock
              btnStyle={hasCartItems ? 'secondary' : 'primary'}
              className="mr-0 mr-lg-2 mb-2 mb-lg-0"
              onClick={onAddToCart}
            >
              {hasCartItems ? 'Added to cart!' : 'Add to cart'}
              {hasCartItems && (
                <CheckboxIcon className="btn-purchase-options-in-cart-check" />
              )}
            </Button>
          )}
          <Button
            btnBlock
            btnStyle="success"
            className="mt-0 d-flex align-items-center"
            onClick={onCheckout}
            disabled={checkingOut}
          >
            {checkingOut ? (
              <>Checking out...</>
            ) : (
              <>
                <PostcardIcon
                  id="account-card-review-post-card-icon"
                  color="white"
                  className="mr-1"
                />{' '}
                Looks good, send now!
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

AccountCardReview.propTypes = propTypes;

export default AccountCardReview;
